import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@tanium/react-box';
import { Link } from '@tanium/react-link';
import { Panel } from '@tanium/react-panel';
import { Stack } from '@tanium/react-stack';
import { Button } from '@tanium/react-button';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { RibbonColumn, StackedRibbonList } from '@tanium/react-stacked-ribbon-list';
import { EditIcon, TrashIcon, AddIcon } from '@tanium/react-graphics';
import { HostTargetAccess } from 'gql/HostTargetAccess';
import ConfirmationModal from 'components/pages/request/requestPartials/ConfirmationModal';
import Helper from 'utils/Helper';
import { TARGET_GROUP_ROUTE } from 'Route';
import InlineCopyToClipboard from 'components/common/copyToClipboard/inlineCopyToClipboard';
import HostTargetAccessSelectors from 'components/pages/console/store/HostTargetAccessSelectors';
import HostTargetAccessSlice from 'components/pages/console/store/HostTargetAccessSlice';
import HostTargetAccessCRUDModal, { Operation } from './hostTargetAccessCRUDModal';

type HostTargetAccessProps = {
  targetOrGroupID: string;
  allowEditing: boolean;
};

const HostTargetAccessList: React.VFC<HostTargetAccessProps> = ({ targetOrGroupID, allowEditing }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const loading = useSelector(HostTargetAccessSelectors.selectHostTargetAccessLoading);
  const mutationSucceeded = useSelector(HostTargetAccessSelectors.selectMutationSucceeded);
  const hostAccess = useSelector(HostTargetAccessSelectors.selectHostTargetAccesses);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedHost, setSelectedAwsTargetRole] = useState<HostTargetAccess | undefined>(undefined);
  const columns: RibbonColumn<HostTargetAccess>[] = useMemo(
    () => [
      {
        label: t('ACTIONS'),
        dataKey: 'id',
        grow: 0,
        width: 50,
        isSortDisabled: true,
        renderer: (data) => {
          return allowEditing && data.itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <div>
              <TooltippedIconButton
                data-testid={`hostTargetAccess-edit-${data.index}`}
                tooltipContent={t('TARGET_ACCESS_EDIT')}
                label={t('TARGET_ACCESS_EDIT')}
                height={16}
                width={16}
                icon={EditIcon}
                onClick={() => {
                  setSelectedAwsTargetRole(data.itemData);
                  setIsOpen(true);
                }}
              />
              <TooltippedIconButton
                data-testid={`hostTargetAccess-delete-${data.index}`}
                style={{ marginLeft: '0.5em' }}
                tooltipContent={t('TARGET_ACCESS_DELETE')}
                label={t('TARGET_ACCESS_DELETE')}
                height={16}
                width={16}
                icon={TrashIcon}
                onClick={() => {
                  setSelectedAwsTargetRole(data.itemData);
                  setIsDeleteConfirmationOpen(true);
                }}
              />
            </div>
          ) : (
            <></>
          );
        },
      },
      {
        label: t('HOST_TARGET_ACCESS_HOSTNAME_LABEL'),
        dataKey: 'hostName',
        primary: true,
      },
      {
        label: t('ID'),
        dataKey: 'host-access-id',
        renderer: ({ itemData }) => {
          return (
            <div>
              <InlineCopyToClipboard
                displayedText={itemData.id}
                copyText={itemData.id}
                iconSize={{ width: 16, height: 16 }}
                tooltipContent={t('COPY_ID')}
              />
            </div>
          );
        },
      },
      {
        label: t('HOST_TARGET_ACCESS_USERNAME_LABEL'),
        dataKey: 'userName',
      },
      {
        label: t('DESCRIPTION'),
        dataKey: 'description',
      },
      {
        label: t('AD_GROUPS'),
        dataKey: 'adGroups',
        renderer: ({ itemData }) => {
          return itemData.adGroups.join(', ');
        },
      },
      {
        label: t('GRAPHICAL_ACCESS'),
        dataKey: 'graphicalAccess',
        renderer: ({ itemData }) => {
          return itemData.graphicalAccess ? 'true' : 'false';
        },
      },
      {
        label: t('PARENT_ID'),
        dataKey: 'targetOrTargetGroupID',
        renderer: ({ itemData }) => {
          return itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <>{t('SELF')}</>
          ) : (
            <Link
              data-testid={`link-${itemData.id}`}
              href={Helper.interpolateRoute(TARGET_GROUP_ROUTE.url, { targetGroupId: itemData.targetOrTargetGroupID })}
            >
              {itemData.targetOrTargetGroupID}
            </Link>
          );
        },
      },
    ],
    [setIsDeleteConfirmationOpen, setSelectedAwsTargetRole, setIsOpen, targetOrGroupID, t, allowEditing]
  );

  const createHostTargetAccess = useCallback(() => {
    setSelectedAwsTargetRole(undefined);
    setIsOpen(true);
  }, [setIsOpen]);

  const dataGetter = useCallback(
    (index: number): HostTargetAccess => {
      return hostAccess[index];
    },
    [hostAccess]
  );

  const itemIdGetter = useCallback(
    (index: number) => {
      return dataGetter(index).id;
    },
    [dataGetter]
  );

  const isItemLoaded = useCallback(
    (index: number) => {
      return !!dataGetter(index);
    },
    [dataGetter]
  );

  const isIdEmphasized = useCallback(
    (id: string) => {
      const item = hostAccess.find((role) => role.id === id);
      return item?.targetOrTargetGroupID === targetOrGroupID;
    },
    [hostAccess, targetOrGroupID]
  );

  const handleDelete = useCallback(
    (proceed: boolean) => {
      if (proceed && selectedHost !== undefined) {
        dispatch(HostTargetAccessSlice.actions.deleteHostTargetAccess(selectedHost.id));
      }

      setIsDeleteConfirmationOpen(false);
      setSelectedAwsTargetRole(undefined);
    },
    [setIsDeleteConfirmationOpen, setSelectedAwsTargetRole, selectedHost, dispatch]
  );

  const handleCreateOrUpdate = useCallback(
    (operation: Operation) => {
      setIsOpen(false);

      if (operation.type === 'cancel') {
        return;
      }

      switch (operation.type) {
        case 'create':
          dispatch(
            HostTargetAccessSlice.actions.createHostTargetAccess({
              adGroups: operation.adGroups,
              hostName: operation.hostName,
              userName: operation.userName,
              graphicalAccess: operation.graphicalAccess,
              targetOrTargetGroupID: operation.targetOrTargetGroupId,
              description: operation.description,
            })
          );
          break;
        case 'update':
          dispatch(
            HostTargetAccessSlice.actions.updateHostTargetAccess({
              adGroups: operation.adGroups,
              hostName: operation.hostName,
              userName: operation.userName,
              graphicalAccess: operation.graphicalAccess,
              targetOrTargetGroupID: operation.targetOrTargetGroupId,
              description: operation.description,
              id: operation.id,
            })
          );
          break;
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (mutationSucceeded) {
      dispatch(
        HostTargetAccessSlice.actions.listHostTargetAccessForTargetOrGroup({
          includeAllTargetGroups: true,
          targetOrTargetGroupID: targetOrGroupID,
        })
      );
    }
  }, [dispatch, targetOrGroupID, mutationSucceeded]);

  const listActions = allowEditing ? (
    <Stack>
      <Button data-testid="hostTargetAccess-create" size="sm" onClick={createHostTargetAccess}>
        <Box pr={1} display={'inline-block'}>
          <AddIcon height={'10px'} />
        </Box>
        {t('CREATE')}
      </Button>
    </Stack>
  ) : (
    <></>
  );

  return (
    <Panel title={t('HOST_TARGET_ACCESS_TITLE')}>
      <Stack direction="column" itemSpacing={1} data-testid="hostTargetAccess">
        <StackedRibbonList
          enableRowHighlight={false}
          columns={columns}
          isItemLoaded={isItemLoaded}
          height={hostAccess.length > 0 ? '12em' : '7.4em'}
          dataGetter={dataGetter}
          itemCount={hostAccess.length}
          itemIdGetter={itemIdGetter}
          listActions={listActions}
          isIdEmphasized={isIdEmphasized}
          isLoading={loading}
        />
        <HostTargetAccessCRUDModal
          isOpen={isOpen}
          hostToEdit={selectedHost}
          onProcess={handleCreateOrUpdate}
          targetOrTargetGroupId={targetOrGroupID}
        />
        <ConfirmationModal
          timeoutSeconds={1}
          headerText={t('HOST_TARGET_ACCESS_DELETE_HEADER')}
          confirmationText={t('DELETE')}
          closeModal={handleDelete}
          isOpen={isDeleteConfirmationOpen}
        >
          <>{t('HOST_TARGET_ACCESS_DELETE_CONTENT')}</>
        </ConfirmationModal>
      </Stack>
    </Panel>
  );
};

export default HostTargetAccessList;
